import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { yupResolver } from '@hookform/resolvers/yup'
import { fetchStore, updateStore } from 'utils/api/storeApi'
import { PAGE_NOT_FOUND, PAGE_STORE_MGMT } from 'utils/constants'
import { Button, Spinner } from 'components/common'
import { StoreDetailsForm } from 'components/storeManagement'
import BackButton from 'components/backButton'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { updateStoreSchema } from 'utils/schema/storeSchema'
import { StoreFormData } from 'components/storeManagement/utils'
import { handleError } from 'utils/helpers/handleError'
import { usePermission } from 'hooks'
import { PERMISSIONS } from 'utils/constants'

export type UpdateStoreFormData = Omit<StoreFormData, 'storeId'> & {
  storeId?: string
}

const UpdateStorePage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { hasPermission } = usePermission()
  const [loading, setLoading] = useState<boolean>(true)
  const [store, setStore] = useState<any>({})

  const canEditSapStore = hasPermission(PERMISSIONS.STORE_SAP_CONFIG_EDIT)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UpdateStoreFormData>({
    resolver: yupResolver(updateStoreSchema),
  })

  const getStoreDetails = async (id: string) => {
    try {
      const { data } = await fetchStore(id)
      data.storeId = data._id
      if (data?.location?.coordinates) {
        const [lng, lat] = data.location.coordinates
        data.location.coordinates = `${lat},${lng}`
      }
      setStore(data)
      delete data._id
      reset(data)
    } catch (error) {
      setStore({})
      navigate(PAGE_NOT_FOUND.path)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      getStoreDetails(id)
    }
  }, [])

  if (loading) {
    return <Spinner />
  }

  // UpdateStorePage
  const handleUpdateStore = async (data: UpdateStoreFormData) => {
    if (!id) return

    const toastStore = toast.loading('Updating Store...', {
      duration: Infinity,
    })

    try {
      delete data.storeId

      if (!canEditSapStore) {
        delete (data as any).sapStoreCode
      }

      if (!canEditSapStore) {
        delete (data as any).sapStoreCode
      }

      if (!canEditSapStore) {
        delete (data as any).sapStoreCode
      }

      const isThirdParty = data?.isThirdPartyStore === true

      const payload: any = {
        ...data,
        lastMile: isThirdParty ? undefined : data.lastMile,
        invoicePrefixCode: isThirdParty ? undefined : data.invoicePrefixCode,
        orderErpSource: isThirdParty ? undefined : (data as any).orderErpSource,
        instashopIdentifier: isThirdParty
          ? undefined
          : (data as any).instashopIdentifier,
        talabatId: isThirdParty ? undefined : (data as any).talabatId,
        flowerTalabatId: isThirdParty
          ? undefined
          : (data as any).flowerTalabatId,
        foodTalabatId: isThirdParty ? undefined : (data as any).foodTalabatId,
      }

      await updateStore(id, payload)
      toast.success('Store updated successfully!', {
        id: toastStore,
        duration: 3000,
      })
      reset()
      navigate(PAGE_STORE_MGMT.path)
    } catch (error) {
      handleError(error, toastStore, { default: 'Failed to update store!' })
    }
  }

  return (
    <>
      <BackButton />

      <form onSubmit={handleSubmit(handleUpdateStore)}>
        <StoreDetailsForm
          setValue={setValue}
          register={register}
          errors={errors}
          store={store}
          isOptional
          canEditSapStore={canEditSapStore}
        />

        <Button buttonText="Update Store" className="mt-7 w-44" />
      </form>
    </>
  )
}

export default UpdateStorePage
